import React, { useState, lazy } from 'react';
import { Plus, Check } from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';
import { Product } from '../../data/mockProducts';
import { TrustBadge } from '../ui/TrustBadge';
import { useCart } from '../../hooks/useCart';
interface ProductCardProps {
  product: Product;
  index?: number;
}
export function ProductCard({
  product,
  index = 0
}: ProductCardProps) {
  const {
    addToCart
  } = useCart();
  const [isAdded, setIsAdded] = useState(false);
  const handleAdd = () => {
    addToCart(product);
    setIsAdded(true);
    setTimeout(() => setIsAdded(false), 1500);
  };
  return <motion.div initial={{
    opacity: 0,
    y: 20
  }} whileInView={{
    opacity: 1,
    y: 0
  }} viewport={{
    once: true,
    margin: '-50px'
  }} transition={{
    duration: 0.5,
    delay: index * 0.05,
    ease: [0.25, 0.1, 0.25, 1]
  }} className="bg-white rounded-2xl shadow-sm border border-gray-100 overflow-hidden flex flex-col h-full relative group" data-id="element-158">
      {/* Image */}
      <div className="aspect-square relative bg-gray-50 overflow-hidden" data-id="element-159">
        <img src={product.image} alt={product.altText} className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-105" loading="lazy" data-id="element-160" />
        {/* Badges */}
        <div className="absolute top-3 left-3 flex flex-col gap-2" data-id="element-161">
          {product.isWIC && <TrustBadge type="WIC" size="sm" data-id="element-162" />}
          {product.isEBT && <TrustBadge type="EBT" size="sm" data-id="element-163" />}
        </div>
      </div>

      {/* Content */}
      <div className="p-5 flex flex-col flex-grow" data-id="element-164">
        <div className="mb-2" data-id="element-165">
          <span className="text-xs text-gray-500 font-semibold uppercase tracking-wider" data-id="element-166">
            {product.category}
          </span>
        </div>
        <h3 className="font-bold text-gray-900 text-base leading-snug mb-3 line-clamp-2 min-h-[2.5rem]" data-id="element-167">
          {product.name}
        </h3>

        <div className="mt-auto pt-4 flex items-end justify-between gap-3" data-id="element-168">
          <div className="flex flex-col gap-1" data-id="element-169">
            <span className="text-xl font-bold text-[#2D5F3F]" data-id="element-170">
              ${product.price.toFixed(2)}
            </span>
            {product.isEBT && <span className="text-xs text-gray-500 font-medium" data-id="element-171">
                EBT Eligible
              </span>}
          </div>

          <motion.button whileTap={{
          scale: 0.9
        }} onClick={handleAdd} className={`h-12 w-12 rounded-full flex items-center justify-center shadow-lg transition-colors flex-shrink-0 ${isAdded ? 'bg-[#2D5F3F] text-white' : 'bg-[#D97757] text-white hover:bg-[#c66545]'}`} aria-label={`Add ${product.name} to cart`} data-id="element-172">
            <AnimatePresence mode="wait" data-id="element-173">
              {isAdded ? <motion.div key="check" initial={{
              scale: 0,
              rotate: -180
            }} animate={{
              scale: 1,
              rotate: 0
            }} exit={{
              scale: 0,
              rotate: 180
            }} transition={{
              type: 'spring',
              stiffness: 300,
              damping: 20
            }} data-id="element-174">
                  <Check className="w-6 h-6" data-id="element-175" />
                </motion.div> : <motion.div key="plus" initial={{
              scale: 0,
              rotate: 180
            }} animate={{
              scale: 1,
              rotate: 0
            }} exit={{
              scale: 0,
              rotate: -180
            }} transition={{
              type: 'spring',
              stiffness: 300,
              damping: 20
            }} data-id="element-176">
                  <Plus className="w-6 h-6" data-id="element-177" />
                </motion.div>}
            </AnimatePresence>
          </motion.button>
        </div>
      </div>
    </motion.div>;
}